/**
 * Skyline from Pakistan to Japan: K2, Badshahi Mosque and Minar-e-Pakistan on the left,
 * a plane flying the route, then a torii gate, five-storey pagoda, Mt. Fuji and Tokyo Tower.
 * The two mountains echo the logo (K2 in green, Fuji in red). Pure SVG, no images.
 */
export function Skyline({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 1440 170"
      preserveAspectRatio="xMidYMax slice"
      className={className}
      role="img"
      aria-label="Skyline from Lahore to Tokyo: K2, Badshahi Mosque, Minar-e-Pakistan, a plane, a torii gate, a pagoda, Mt. Fuji and Tokyo Tower"
    >
      <defs>
        <linearGradient id="sky-k2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1b6a54" />
          <stop offset="1" stopColor="#0E4B3B" />
        </linearGradient>
        <linearGradient id="sky-fuji" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#a8304d" />
          <stop offset="1" stopColor="#6e1029" />
        </linearGradient>
        <path id="sky-flight" d="M600 112 C 690 10, 820 0, 905 70" />
      </defs>

      {/* far hills */}
      <path d="M0 150 C 180 120 300 140 480 128 S 820 140 1000 126 S 1300 132 1440 120 V170 H0Z" fill="#1d2c4c" />

      {/* K2 */}
      <path d="M-10 170 L120 70 L150 82 L196 22 L236 64 L262 54 L380 170Z" fill="url(#sky-k2)" />
      <path d="M196 22 L176 48 L188 46 L196 58 L206 44 L222 52 Z" fill="#fff" opacity=".9" />
      <path d="M120 70 L108 82 L124 80 L132 88 L140 78 L150 82Z" fill="#fff" opacity=".6" />

      {/* Badshahi Mosque */}
      <g fill="#14213D">
        <rect x="262" y="128" width="190" height="42" />
        <rect x="248" y="72" width="9" height="98" />
        <rect x="457" y="72" width="9" height="98" />
        <path d="M246 72 h13 l-6.5 -12z M455 72 h13 l-6.5 -12z" />
        <rect x="244" y="96" width="17" height="4" />
        <rect x="453" y="96" width="17" height="4" />
        <path d="M300 128 c0 -22 12 -30 18 -38 c6 8 18 16 18 38z" />
        <path d="M332 128 c0 -30 16 -40 25 -52 c9 12 25 22 25 52z" />
        <path d="M378 128 c0 -22 12 -30 18 -38 c6 8 18 16 18 38z" />
        <rect x="355.5" y="64" width="3" height="12" />
        <rect x="316.5" y="80" width="3" height="10" />
        <rect x="394.5" y="80" width="3" height="10" />
        <path d="M262 128 h190 v6 h-190z" fill="#22335a" />
      </g>

      {/* Minar-e-Pakistan */}
      <g fill="#14213D">
        <path d="M526 170 L532 138 L544 138 L548 40 L556 40 L560 138 L572 138 L578 170Z" />
        <path d="M512 150 q40 -26 80 0 v8 h-80z" />
        <path d="M546 40 q6 -16 12 0z" />
        <rect x="551" y="18" width="2" height="12" />
        <rect x="543" y="88" width="18" height="4" />
      </g>

      {/* flight path + plane */}
      <use href="#sky-flight" fill="none" stroke="#C9A66B" strokeOpacity=".55" strokeWidth="1.5" strokeDasharray="2 7" strokeLinecap="round" />
      <circle cx="600" cy="112" r="3" fill="#C9A66B" />
      <circle cx="905" cy="70" r="3" fill="#BB143A" />
      <g>
        <path
          d="M-9 0 L9 0 M2 0 L-4 -8 M2 0 L-4 8 M-8 0 L-11 -4 M-8 0 L-11 4"
          stroke="#fff"
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
        />
        <animateMotion dur="10s" repeatCount="indefinite" rotate="auto" keyPoints="0;1" keyTimes="0;1" calcMode="spline" keySplines=".45 0 .55 1">
          <mpath href="#sky-flight" />
        </animateMotion>
        <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;.08;.9;1" dur="10s" repeatCount="indefinite" />
      </g>

      {/* torii */}
      <g fill="#BB143A">
        <path d="M948 104 q48 -9 96 0 l-3 7 q-45 -8 -90 0z" />
        <rect x="954" y="116" width="84" height="5" />
        <rect x="962" y="110" width="7" height="60" />
        <rect x="1023" y="110" width="7" height="60" />
        <rect x="993" y="111" width="6" height="7" />
      </g>

      {/* Mt. Fuji */}
      <path d="M1040 170 L1170 52 L1196 48 L1222 52 L1360 170Z" fill="url(#sky-fuji)" />
      <path d="M1170 52 L1148 74 L1160 72 L1170 84 L1182 70 L1196 82 L1208 70 L1222 80 L1234 72 L1244 74 L1222 52 L1196 48Z" fill="#fff" opacity=".92" />

      {/* five-storey pagoda */}
      <g fill="#14213D">
        <rect x="1088" y="66" width="2" height="16" />
        {[0, 1, 2, 3, 4].map((i) => {
          const y = 82 + i * 18
          const w = 30 + i * 6
          return (
            <g key={i}>
              <path d={`M${1089 - w / 2 - 8} ${y + 6} q${w / 2 + 8} -10 ${w + 16} 0 l-4 3 h${-(w + 8)}z`} />
              <rect x={1089 - w / 2 + 4} y={y + 8} width={w - 8} height={10} />
            </g>
          )
        })}
        <rect x="1067" y="170" width="44" height="0" />
      </g>

      {/* Tokyo Tower */}
      <g stroke="#BB143A" strokeWidth="2.2" fill="none" strokeLinejoin="round">
        <path d="M1372 170 L1394 64 L1402 24 L1410 64 L1432 170" />
        <path d="M1380 132 h44 M1386 104 h32 M1392 76 h20" />
        <path d="M1380 132 L1418 104 M1424 132 L1386 104 M1386 104 L1412 76 M1418 104 L1392 76" strokeWidth="1.2" />
        <path d="M1402 24 v-14" />
      </g>
      <rect x="1388" y="96" width="28" height="7" fill="#fff" opacity=".85" />

      {/* ground line */}
      <rect x="0" y="166" width="1440" height="4" fill="#14213D" />
    </svg>
  )
}
